import Dropzone from "react-dropzone";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { faFile } from "@fortawesome/free-regular-svg-icons";
import { DoraLinkButton, DoraLink } from "dora-shared/frontend";
import "./DocumentsCard.scss";
import axios from "../../../../axios";
import axiosRaw from "axios";
import ExistingCargoDocumentsList from "./ExistingCargoDocumentsList";
import { useMemo, useState } from "react";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import { notify } from "../../../../ducks/notifications";
import { loadDocuments } from "../../../../ducks/data/cargo-documents";
import { openCmrPdf } from "../../../../ducks/data/files";
import GenericPromptDialog from "../../GenericPromptDialog/GenericPromptDialog";
import { useFeature } from "../../../../hooks";
import { selectCargoEvents } from "../../../../ducks/data/cargo-events/selectors";
import { selectInitialFormData } from "../../../../ducks/app/cargo-dialog/selectors";
import { useFormContext, useWatch } from "react-hook-form";
import CreateShipmentData, {
  CreateEditShipmentStop,
} from "../CreateEditShipmentData";
import CmrConfigsDialog from "./CmrConfigsDialog";
import useDropEmptyFiles from "./use-drop-empty-files";

const stopAddress = (stop: CreateEditShipmentStop | undefined) =>
  stop?.place?.placeName || stop?.place?.address || stop?.place?.city || "";

const findStopById = (list: CreateEditShipmentStop[], id: string) =>
  list.find((s) => (s.id || s.provisoryId) === id);

type DocumentsCardProps = {
  createNew: boolean;
  cargoId: string | null;
  pickupNumber: number;
  dropoffNumber: number;
  previewMode: boolean;
};

const DocumentsCard = (props: DocumentsCardProps) => {
  const { createNew, cargoId, pickupNumber, dropoffNumber, previewMode } =
    props;
  const [documentIdToDelete, setDocumentIdToDelete] = useState<string | null>(
    null
  );
  const [documentsChangeInProgress, setDocumentsChangeInProgress] =
    useState(false);
  const { t } = useTranslation(["shipmentModal", "components"]);
  const areDriverLogDocsEnabled = useFeature("driver-report-for-cargo");
  const liteVersion = useFeature("lite-version");
  const manyToManyEnabled = useFeature("pickups-dropoffs-many-to-many");
  const dispatch = useAppDispatch();
  const dropEmptyFiles = useDropEmptyFiles();
  const cargoEvents = useSelector(selectCargoEvents(cargoId));
  const initialFormData = useSelector(selectInitialFormData);
  const { control } = useFormContext<CreateShipmentData>();
  const cmrConfigs = useWatch({ control, name: "cmrConfigs" });
  const pickupList = useWatch({ control, name: "pickupList" });
  const dropoffList = useWatch({ control, name: "dropoffList" });
  const [cmrConfigsDialogOpen, setCmrConfigsDialogOpen] = useState(false);
  const showDriverLogLinks = useMemo(
    () =>
      !!cargoEvents?.filter((e) =>
        [
          "CARGO_COLLECTED",
          "CARGO_DELIVERED",
          "DRIVER_ARRIVED",
          "DRIVER_DEPARTED",
        ].includes(e.type)
      ).length,
    [cargoEvents]
  );

  const printCmrPdf = (
    options:
      | { pickupIndex: number; dropoffIndex: number }
      | { cmrConfigId: string }
  ) => {
    if ("cmrConfigId" in options) {
      dispatch(openCmrPdf({ cmrConfigId: options.cmrConfigId }));
    } else if (cargoId) {
      dispatch(
        openCmrPdf({
          cargoId,
          pickup: options.pickupIndex,
          dropoff: options.dropoffIndex,
        })
      );
    }
  };

  const onFilesAdded = async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      dispatch(
        notify({
          message: t(
            `shipmentModal:cards.documents.notificationErrorNotAccepted`
          ),
          type: "error",
        })
      );
      return;
    }
    const files = dropEmptyFiles(acceptedFiles);
    if (!files.length) {
      return;
    }

    setDocumentsChangeInProgress(true);

    try {
      const fileReferences = [];

      for (const file of files) {
        // Step 1: Request signed URL from server
        const urlResponse = await axios.post(
          `/api/cargos/document-upload-url`,
          {
            contentType: file.type,
          }
        );
        const { uploadUrl, s3Identifier } = urlResponse.data;

        // Step 2: Upload a file directly to S3 (use raw axios without custom headers)
        const buffer = await file.arrayBuffer();
        await axiosRaw.put(uploadUrl, buffer, {
          headers: {
            "Content-Type": file.type,
          },
        });

        // Collect file reference data
        fileReferences.push({
          s3Identifier,
          name: file.name,
          mimeType: file.type,
        });
      }

      // Step 3: Save file references in database
      await axios.post(`/api/cargos/${cargoId}/document-reference`, {
        files: fileReferences,
      });
      dispatch(
        notify({
          message: t(`cards.documents.notificationSuccess`, {
            count: files.length,
          }),
          type: "success",
        })
      );
    } catch (err) {
      console.error(err);
      dispatch(
        notify({
          message: t(`cards.documents.notificationError`, {
            count: files.length,
          }),
          type: "error",
        })
      );
    } finally {
      setDocumentsChangeInProgress(false);
      dispatch(loadDocuments(cargoId as string));
    }
  };

  const removeDocument = async () => {
    setDocumentsChangeInProgress(true);
    await axios
      .delete(`/api/cargos/${cargoId}/documents/${documentIdToDelete}`)
      .then(() => {
        dispatch(
          notify({
            message: t(
              "shipmentModal:cards.documents.notificationRemoveSuccess"
            ),
            type: "success",
          })
        );
        setDocumentIdToDelete(null);
        dispatch(loadDocuments(cargoId as string));
      })
      .catch((err) => {
        console.error(err);
        dispatch(
          notify({
            message: t("shipmentModal:cards.documents.notificationRemoveError"),
            type: "error",
          })
        );
      })
      .finally(() => {
        setDocumentsChangeInProgress(false);
      });
  };

  let idx = 1;

  // A config can only be downloaded once the cargo (and the config itself)
  // has been persisted - before the first save, cmrConfigs only carry
  // client-generated temporary ids with nothing on the server to render a
  // PDF from. The list is still shown (so the user sees what they've
  // configured before saving), just disabled until then.
  const cmrConfigsDownloadable = !createNew || previewMode;
  // The same holds per config on an existing cargo: one added in this dialog
  // has a temporary id until the cargo is saved (DORA-481, SERVER-15D). The
  // last loaded cargo - reloaded after every save - has the saved ones.
  const savedCmrConfigIds = useMemo(
    () => new Set((initialFormData?.cmrConfigs || []).map((c) => c.id)),
    [initialFormData]
  );
  const isCmrConfigDownloadable = (id: string) =>
    cmrConfigsDownloadable && (previewMode || savedCmrConfigIds.has(id));

  // Once pickups-dropoffs-many-to-many is on, CMR generation is driven
  // entirely by the explicit cmrConfigs list (configured via the "Configure
  // CMR" modal) instead of the derived-index links below - a cargo can have
  // more than one pickup and more than one dropoff at once, where "pickup
  // index 0, dropoff index 0" no longer uniquely identifies a pairing.
  const cmrConfigDownloads = manyToManyEnabled
    ? (cmrConfigs || []).map((c) => {
        const from = stopAddress(findStopById(pickupList, c.pickupId));
        const to = c.dropoffId
          ? stopAddress(findStopById(dropoffList, c.dropoffId))
          : t("shipmentModal:cards.documents.cmrConfig.noDropoff");
        return (
          <div className="documents-card__list-item" key={c.id}>
            <DoraLinkButton
              disabled={!isCmrConfigDownloadable(c.id)}
              onClick={
                isCmrConfigDownloadable(c.id)
                  ? () => printCmrPdf({ cmrConfigId: c.id })
                  : undefined
              }
            >
              {t("shipmentModal:cards.documents.cmrConfig.downloadLabel", {
                from,
                to,
              })}
            </DoraLinkButton>
          </div>
        );
      })
    : null;

  const extraPickupsCmrDownloads =
    !manyToManyEnabled && pickupNumber > 1
      ? new Array(pickupNumber - 1).fill(0).map((_, i) => (
          <div className="documents-card__list-item" key={i}>
            <DoraLinkButton
              onClick={() =>
                printCmrPdf({ pickupIndex: i + 1, dropoffIndex: 0 })
              }
            >
              {t("shipmentModal:cards.documents.downloadCmrX", { id: idx++ })}
            </DoraLinkButton>
          </div>
        ))
      : null;
  const extraDropoffsCmrDownloads =
    !manyToManyEnabled && dropoffNumber > 1
      ? new Array(dropoffNumber - 1).fill(0).map((_, i) => (
          <div className="documents-card__list-item" key={i}>
            <DoraLinkButton
              onClick={() =>
                printCmrPdf({ pickupIndex: 0, dropoffIndex: i + 1 })
              }
            >
              {t("shipmentModal:cards.documents.downloadCmrX", { id: idx++ })}
            </DoraLinkButton>
          </div>
        ))
      : null;

  return (
    <div className="documents-card">
      {documentIdToDelete && (
        <GenericPromptDialog
          title={t("shipmentModal:cards.documents.confirmRemoveDocumentTitle")}
          text={t("shipmentModal:cards.documents.confirmRemoveDocumentMessage")}
          continueButtonColor={"error"}
          continueText={t("components:buttonLabels.remove")}
          onCancelClick={() => setDocumentIdToDelete(null)}
          onContinueClick={() => removeDocument()}
        />
      )}
      <div className="flex justify-between items-center">
        <h3 className="documents-card__title">
          {t("shipmentModal:cards.documents.title")}
        </h3>
        {!liteVersion && !createNew && (
          <Dropzone
            maxSize={50000000}
            accept={{
              "image/png": [".png"],
              "image/jpeg": [".jpeg", ".jpg"],
              "application/pdf": [".pdf"],
              "application/vnd.ms-word": [".doc", ".docx"],
              "application/vnd.ms-excel": [".xls", ".xlsx"],
              "application/vnd.apple.numbers": [".numbers"],
              "message/rfc822": [".eml"],
              "application/vnd.ms-outlook": [".pst", ".ost"],
              "text/calendar": [".ics"],
            }}
            onDrop={onFilesAdded}
            disabled={documentsChangeInProgress}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <DoraLinkButton
                  className="flex gap-2"
                  disabled={documentsChangeInProgress}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {t("shipmentModal:cards.documents.upload")}
                </DoraLinkButton>
              </div>
            )}
          </Dropzone>
        )}
      </div>
      {createNew && !previewMode && (
        <div className="documents-card__placeholder-text">
          {liteVersion
            ? "Opret ordren for at se CMR"
            : t("shipmentModal:cards.documents.availableLater")}
        </div>
      )}
      <div className="documents-card__list">
        {/* Configuring CMR links only touches form state (pickup/dropoff ids,
            provisional or not) - unlike everything else in this card, it
            doesn't need a persisted cargo, so it stays available while
            creating a new shipment. */}
        {manyToManyEnabled && (
          <div className="documents-card__list-item mb-2">
            <DoraLinkButton
              className="flex items-center gap-2 whitespace-nowrap"
              onClick={() => setCmrConfigsDialogOpen(true)}
            >
              <FontAwesomeIcon icon={faFile} />
              {t("shipmentModal:cards.documents.cmrConfig.configureButton")}
            </DoraLinkButton>
          </div>
        )}
        {cmrConfigDownloads}
        {(!createNew || previewMode) && (
          <>
            {areDriverLogDocsEnabled && showDriverLogLinks && (
              <>
                <div className="documents-card__list-item">
                  <DoraLink
                    className="flex items-center gap-2 whitespace-nowrap"
                    url={`/api/mileage-reports/pdf-for-cargo?cargoId=${cargoId}`}
                    newTab
                  >
                    <FontAwesomeIcon icon={faFile} />
                    {t("shipmentModal:cards.documents.downloadDriverReportPDF")}
                  </DoraLink>
                </div>
                <div className="documents-card__list-item">
                  <DoraLink
                    className="flex items-center gap-2 whitespace-nowrap"
                    url={`/api/mileage-reports/csv-for-cargo?cargoId=${cargoId}`}
                    newTab
                  >
                    <FontAwesomeIcon icon={faFile} />
                    {t("shipmentModal:cards.documents.downloadDriverReportCSV")}
                  </DoraLink>
                </div>
              </>
            )}
            {!manyToManyEnabled && (
              <div className="documents-card__list-item">
                <DoraLinkButton
                  className="flex items-center gap-2 whitespace-nowrap"
                  onClick={() =>
                    printCmrPdf({ pickupIndex: 0, dropoffIndex: 0 })
                  }
                >
                  <FontAwesomeIcon icon={faFile} />
                  {t("shipmentModal:cards.documents.downloadCmr")}
                </DoraLinkButton>
              </div>
            )}
            {extraPickupsCmrDownloads} {extraDropoffsCmrDownloads}
          </>
        )}
        {cargoId && (
          <ExistingCargoDocumentsList
            onRemoveItemClick={setDocumentIdToDelete}
            cargoId={cargoId}
            previewMode={previewMode}
          />
        )}
      </div>
      {cmrConfigsDialogOpen && (
        <CmrConfigsDialog onClose={() => setCmrConfigsDialogOpen(false)} />
      )}
    </div>
  );
};

export default DocumentsCard;
